import { useCallback } from 'react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import useTakerMarkets, {
  TakerMarket,
  fetchTakerMarketsWithoutUserSuppliers as fetchTakerMarkets,
} from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import { createTakerMarketUuidMap } from '@/utils/takerMarketUuidMap';

interface MarketStat {
  marketUuid: string;
  takerId: number;
  time: number;
}

export interface ServerSentEventListeners {
  marketStats: MarketStat[];
}

const removePricingSuggestionQueries = (queryClient: QueryClient, takerMarkets: TakerMarket[]) => {
  takerMarkets.forEach((tm) => {
    queryClient.removeQueries({
      predicate: (query) => {
        return query.queryKey.includes('pricing-suggestion') && query.queryKey.includes(createTakerMarketUuidMap(tm));
      },
    });
  });
};

const refreshInvoicesAndStats = async (queryClient: QueryClient, takerMarkets: TakerMarket[]) => {
  return Promise.all(
    takerMarkets.flatMap((tm) => {
      return [
        queryClient.refetchQueries({ queryKey: ['playable-invoices', tm.makerOrganizationUuid] }),
        queryClient.refetchQueries({ queryKey: ['playable-invoice-stats', tm.makerOrganizationUuid] }),
        queryClient.refetchQueries({
          queryKey: ['eligible-invoices', tm.marketId, tm.takerDivisionId],
        }),
        queryClient.refetchQueries({
          queryKey: ['eligible-invoice-stats', tm.marketId, tm.takerDivisionId],
        }),
        queryClient.refetchQueries({
          queryKey: ['eligible-invoice-stats-filtered', tm.marketId, tm.takerDivisionId],
        }),
      ];
    })
  );
};

type TakerMarketRef = { marketUuid: string } & (
  | { takerId: number; takerUuid?: never }
  | { takerUuid: string; takerId?: never }
);

export const useRefetchStatsQueries = () => {
  const queryClient = useQueryClient();
  const { data: takerMarkets } = useTakerMarkets();

  const refetchOneTakerMarket = useCallback(
    async ({ marketUuid, takerId, takerUuid }: TakerMarketRef) => {
      const takerMarket = takerMarkets?.find(
        (tm) =>
          tm.marketUuid === marketUuid &&
          (takerId !== undefined ? tm.takerDivisionId === takerId : tm.takerDivisionUuid === takerUuid)
      );

      if (!takerMarket) {
        return;
      }

      // remove any pricing suggestion queries to trigger a refetch
      // when the next component mounts and uses the query
      removePricingSuggestionQueries(queryClient, [takerMarket]);

      try {
        const updatedTakerMarkets = await fetchTakerMarkets({
          filter: {
            supplierMarkets: [
              {
                makerOrganizationUuid: takerMarket.makerOrganizationUuid,
                marketUuid: takerMarket.marketUuid,
                supplierDivisionUuid: takerMarket.takerDivisionUuid,
              },
            ],
          },
        });
        const updatedTakerMarket = updatedTakerMarkets.find(
          (tm) =>
            tm.marketUuid === marketUuid &&
            (takerId !== undefined ? tm.takerDivisionId === takerId : tm.takerDivisionUuid === takerUuid)
        );

        if (updatedTakerMarket) {
          queryClient.setQueryData<TakerMarket[]>(['taker-markets'], (prevTakerMarkets = []) => {
            const takerMarketIndex = prevTakerMarkets.findIndex(
              (ptm) =>
                ptm.marketUuid === updatedTakerMarket.marketUuid &&
                ptm.takerDivisionId === updatedTakerMarket?.takerDivisionId
            );

            if (takerMarketIndex !== -1) {
              const newTakerMarkets = [...prevTakerMarkets];
              newTakerMarkets[takerMarketIndex] = updatedTakerMarket;
              return newTakerMarkets;
            }

            return prevTakerMarkets;
          });
        }
      } catch (error) {
        console.log(error);
      }

      await refreshInvoicesAndStats(queryClient, [takerMarket]);
    },
    [queryClient, takerMarkets]
  );

  const refetchManyTakerMarkets = useCallback(
    async (takerMarketsToRefresh: TakerMarketRef[]) => {
      const existingTakerMarkets = takerMarkets?.filter(
        (tm) =>
          takerMarketsToRefresh.find(
            (tmRefresh) =>
              tmRefresh.marketUuid === tm.marketUuid &&
              (tmRefresh.takerId !== undefined
                ? tmRefresh.takerId === tm.takerDivisionId
                : tmRefresh.takerUuid === tm.takerDivisionUuid)
          ) !== undefined
      );

      if (!existingTakerMarkets || existingTakerMarkets.length !== takerMarketsToRefresh.length) {
        return;
      }

      // Due to URL length restrictions, we can only make a request with ~16 taker markets in the filter
      // so batch these up
      const BATCH_SIZE = 16;

      const allUpdatedTakerMarkets: TakerMarket[] = [];
      let batchStartIdx = 0;
      while (batchStartIdx < existingTakerMarkets.length) {
        const takerMarketBatch = existingTakerMarkets.slice(batchStartIdx, batchStartIdx + BATCH_SIZE);

        try {
          // remove any pricing suggestion queries to trigger a refetch
          // when the next component mounts and uses the query
          removePricingSuggestionQueries(queryClient, takerMarketBatch);

          const updatedTakerMarkets = await fetchTakerMarkets({
            filter: {
              supplierMarkets: takerMarketBatch.map((tm) => ({
                makerOrganizationUuid: tm.makerOrganizationUuid,
                marketUuid: tm.marketUuid,
                supplierDivisionUuid: tm.takerDivisionUuid,
              })),
            },
          });

          if (updatedTakerMarkets) {
            allUpdatedTakerMarkets.push(...updatedTakerMarkets);
          }
        } catch (error) {
          console.log(error);
        }

        batchStartIdx += BATCH_SIZE;
      }

      // Apply all taker market updates in a single setQueryData call so that consumers
      // only re-render once with complete data rather than once per batch.
      if (allUpdatedTakerMarkets.length > 0) {
        queryClient.setQueryData<TakerMarket[]>(['taker-markets'], (prevTakerMarkets = []) => {
          const newTakerMarkets = [...prevTakerMarkets];
          allUpdatedTakerMarkets.forEach((updatedTakerMarket) => {
            const takerMarketIndex = prevTakerMarkets.findIndex(
              (ptm) =>
                ptm.marketUuid === updatedTakerMarket.marketUuid &&
                ptm.takerDivisionId === updatedTakerMarket?.takerDivisionId
            );

            if (takerMarketIndex !== -1) {
              newTakerMarkets[takerMarketIndex] = updatedTakerMarket;
            }
          });

          return newTakerMarkets;
        });
      }

      await refreshInvoicesAndStats(queryClient, existingTakerMarkets);
    },
    [queryClient, takerMarkets]
  );

  return {
    refetchOneTakerMarket,
    refetchManyTakerMarkets,
  };
};

// store in react query for any taker market the FE is waiting to receive a stats update for
export const useMarketStatsStore = () => {
  const { trackEnterpriseEvent } = useReporting();
  const queryClient = useQueryClient();
  const { refetchOneTakerMarket } = useRefetchStatsQueries();

  const updateMarketStatsStore = ({ marketUuid, takerId }: { marketUuid: string; takerId: number | number[] }) => {
    const sseListeners = queryClient.getQueryData<ServerSentEventListeners>(['server-sent-event-listeners']);

    const foundMarketStat = sseListeners?.marketStats.find((marketStat) => {
      if (Array.isArray(takerId)) {
        return marketStat.marketUuid === marketUuid && takerId.includes(marketStat.takerId);
      }

      if (typeof takerId === 'number') {
        return marketStat.marketUuid === marketUuid && marketStat.takerId === takerId;
      }
    });

    if (foundMarketStat) {
      if (Array.isArray(takerId)) {
        trackEnterpriseEvent('stats-update::in-time', {
          marketUuid: foundMarketStat.marketUuid,
          takerId: foundMarketStat.takerId,
        });
      }

      if (typeof takerId === 'number') {
        trackEnterpriseEvent('stats-update::long', {
          marketUuid: foundMarketStat.marketUuid,
          takerId: foundMarketStat.takerId,
        });
      }

      // remove the found market stats listener
      queryClient.setQueryData<ServerSentEventListeners>(['server-sent-event-listeners'], (prevSseListeners) => {
        return {
          ...prevSseListeners,
          marketStats: (prevSseListeners?.marketStats ?? []).filter(
            (marketStat) =>
              marketStat.marketUuid !== foundMarketStat.marketUuid ||
              marketStat.takerId !== foundMarketStat.takerId ||
              marketStat.time !== foundMarketStat.time
          ),
        };
      });

      refetchOneTakerMarket({ marketUuid: foundMarketStat.marketUuid, takerId: foundMarketStat.takerId });
    }
  };

  return updateMarketStatsStore;
};

export const useServerSideEventListeners = () => {
  const queryClient = useQueryClient();
  const updateMarketStatsStore = useMarketStatsStore();

  const listenToMarketStats = (tmMeta: { marketUuid: string; takerId: number }[]) => {
    const sseListeners = queryClient.getQueryData<ServerSentEventListeners>(['server-sent-event-listeners']);
    return sseListeners?.marketStats.some((marketStat) =>
      tmMeta.find(({ marketUuid, takerId }) => marketStat.marketUuid === marketUuid && marketStat.takerId === takerId)
    );
  };

  const listenToAllMarketStats = () => {
    const sseListeners = queryClient.getQueryData<ServerSentEventListeners>(['server-sent-event-listeners']);
    return (sseListeners?.marketStats.length ?? 0) > 0;
  };

  const notifyMarketStatsSubscribers = ({ marketUuid, takerIds }: { marketUuid: string; takerIds: number[] }) => {
    updateMarketStatsStore({ marketUuid, takerId: takerIds });
  };

  const subscribeToMarketStats = ({ marketUuid, takerId }: { marketUuid: string; takerId: number }) => {
    queryClient.setQueryData<ServerSentEventListeners>(['server-sent-event-listeners'], (prevSseListeners) => ({
      ...prevSseListeners,
      marketStats: [
        ...(prevSseListeners?.marketStats ?? []),
        {
          marketUuid,
          takerId,
          time: Date.now(),
        },
      ],
    }));
  };

  return {
    listenToMarketStats,
    listenToAllMarketStats,
    notifyMarketStatsSubscribers,
    subscribeToMarketStats,
  };
};
